/**
 * Jobs Page
 * Lists jobs for clients (their own) or providers (available jobs)
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, useLanguage, API } from "@/context/AppContext";
import { Briefcase, Plus, MapPin, Clock, MessageSquare, Loader2 } from "lucide-react";

const JobsPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data));
    loadJobs();
  }, [selectedCategory]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      if (user?.role === 'client') {
        const res = await axios.get(`${API}/jobs/my`, { withCredentials: true });
        setJobs(res.data);
      } else {
        const url = selectedCategory && selectedCategory !== 'all'
          ? `${API}/jobs?category=${selectedCategory}` 
          : `${API}/jobs`;
        const res = await axios.get(url, { withCredentials: true });
        setJobs(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {user?.role === 'provider' ? t('findJobsTitle') : t('myJobs')}
          </h1>
          <p className="text-gray-500 mt-1">
            {user?.role === 'provider' ? t('browseJobs') : t('manageJobs')}
          </p>
        </div>
        {user?.role === 'client' && (
          <Button 
            onClick={() => navigate('/dashboard/jobs/new')}
            className="bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
            data-testid="new-job-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('newJob')}
          </Button>
        )}
      </div>

      {user?.role === 'provider' && (
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[250px]" data-testid="category-filter">
            <SelectValue placeholder={t('allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allCategories')}</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat[`name_${language}`] || cat.name_en || cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
        </div>
      ) : jobs.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="p-12 text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('noJobsFound')}</p>
            {user?.role === 'client' && (
              <Button 
                onClick={() => navigate('/dashboard/jobs/new')}
                className="mt-4 bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
              >
                {t('createFirstJob')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {jobs.map((job) => (
            <Card 
              key={job.job_id} 
              className="border-gray-200 hover:border-[#0044CC]/30 cursor-pointer transition-colors"
              onClick={() => navigate(`/dashboard/jobs/${job.job_id}`)}
              data-testid={`job-card-${job.job_id}`}
            >
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <h3 className="font-semibold">{job.title}</h3>
                      <Badge className={`text-xs ${
                        job.status === 'open' ? 'bg-green-100 text-green-700' :
                        job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {job.status === 'open' && t('open')}
                        {job.status === 'in_progress' && t('inProgress')}
                        {job.status === 'completed' && t('completed')}
                      </Badge>
                    </div>
                    {/* Client name */}
                    <p className="text-sm text-gray-500 mt-1">von {job.client_name || 'Kunde'}</p>
                    <p className="text-gray-500 text-sm line-clamp-2 mt-2">{job.description}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-3">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {job.location?.city || t('switzerland')}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(job.created_at).toLocaleDateString('de-CH')}
                      </span>
                      {job.bids_count > 0 && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {job.bids_count} {t('bids')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {job.budget_max && (
                      <p className="text-lg font-bold text-[#0044CC]">
                        CHF {job.budget_min ? `${job.budget_min} - ` : ''}{job.budget_max}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobsPage;
